import type { ElementType } from "react";

import { cx, styled } from "simplydo/react/styled";

import type { ImageProps, ImageSize } from "./Image.types";

const SIZE_WIDTH: Record<ImageSize, string> = {
  small: "150px",
  medium: "300px",
  large: "450px",
};

type StyleProps = { $width: string };

const StyledImg = styled.img<StyleProps>`
  width: ${({ $width }) => $width};
  max-width: 100%;
  max-height: 100%;

  display: block;
  margin: 0;
  border-radius: 0;
  vertical-align: middle;
  object-fit: initial;

  &.is-centered {
    display: block;
    margin: 0 auto;
  }

  &.is-circular,
  &.is-avatar {
    border-radius: 500rem;
  }

  &.is-avatar {
    width: 2em;
    height: 2em;
    object-fit: cover;
  }
`;

const StyledImageWrap = styled.div<StyleProps>`
  display: inline-block;
  margin: 0;
  max-width: 100%;
  width: ${({ $width }) => $width};

  &.is-centered {
    display: block;
    margin: 0 auto;
  }

  > img {
    display: block;
    width: 100%;
    height: auto;
    border-radius: 0;
  }

  &.is-circular > img,
  &.is-avatar > img {
    border-radius: 500rem;
  }
`;

function resolveWidth(opts: {
  avatar?: boolean;
  fluid?: boolean;
  size?: ImageSize;
}): string {
  if (opts.avatar) {
    return "2em";
  }
  if (opts.fluid) {
    return "100%";
  }
  if (opts.size) {
    return SIZE_WIDTH[opts.size] || "auto";
  }
  return "auto";
}

const ImageComponent = ({
  as,
  src,
  alt,
  avatar,
  circular,
  fluid,
  centered,
  size,
  href,
  wrapped,
  children,
  className,
  style,
  ...rest
}: ImageProps) => {
  const Tag = (as || (href ? "a" : "img")) as ElementType;
  const isImg = !as && !href && !wrapped;
  const width = rest.width?.toString() || resolveWidth({ avatar, fluid, size });
  const modifierClass = cx(
    centered && "is-centered",
    circular && "is-circular",
    avatar && "is-avatar",
    className,
  );

  if (wrapped || children) {
    return (
      <StyledImageWrap
        as={href ? "a" : "div"}
        href={href}
        className={modifierClass}
        style={style}
        $width={width}
        {...(rest as object)}
      >
        {children || <img src={src} alt={alt ?? ""} />}
      </StyledImageWrap>
    );
  }

  if (isImg) {
    return (
      <StyledImg
        $width={width}
        src={src}
        alt={alt ?? ""}
        className={modifierClass}
        style={style}
        {...(rest as object)}
      />
    );
  }

  return (
    <StyledImg
      $width={width}
      as={Tag}
      src={Tag === "img" ? src : undefined}
      href={href}
      alt={Tag === "img" ? (alt ?? "") : undefined}
      className={modifierClass}
      style={style}
      {...(rest as object)}
    >
      {Tag !== "img" && src ? <img src={src} alt={alt ?? ""} /> : children}
    </StyledImg>
  );
};

export const Image = styled(ImageComponent)``;
